import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Search, Eye, MousePointerClick, MailX, MailMinus, ShieldAlert,
  Users, Loader2, RefreshCw, ChevronRight, X,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Per-recipient drill-down for ONE internal campaign.
 *
 * Sits under the stats tiles on Admin → Email Campaigns. Hits
 * `GET /api/email-campaigns/{id}/recipients` and renders one row per
 * unique email address, with counters + first/last activity timestamps.
 * The filter chips just switch the `event_filter` query param so the
 * server does the heavy lifting (a Mongo aggregation).
 *
 * Auto-refreshes every 30s so the table stays in sync with incoming
 * webhook events without a manual reload.
 */
export default function RecipientDrillDown({ campaignId, refreshSignal }) {
  const [recipients, setRecipients] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [activeEmail, setActiveEmail] = useState(null);

  const load = async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ event_filter: filter, q, limit: "500" });
      const { data } = await api.get(`/email-campaigns/${campaignId}/recipients?${params}`);
      setRecipients(data.recipients || []);
      setTruncated(!!data.truncated);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load recipients");
    } finally { setLoading(false); }
  };

  // Reload when campaign / filter / search changes, plus when the parent
  // page signals a stats refresh (so the drill-down stays in sync).
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, filter, q, refreshSignal]);

  // Soft auto-poll every 30s.
  useEffect(() => {
    if (!campaignId) return;
    const id = setInterval(() => load(), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, filter, q]);

  if (!campaignId) return null;

  return (
    <div data-testid="recipient-drilldown" className="border border-border rounded-sm bg-card">
      <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-base font-bold inline-flex items-center gap-2">
            <Users size={14} className="text-primary" />
            Recipients
            <span className="text-xs font-normal text-muted-foreground">· {recipients.length} {truncated ? "(showing first 500)" : ""}</span>
          </h3>
          <p className="text-[11px] text-muted-foreground mt-1">
            Who specifically opened, clicked, bounced, or unsubscribed — joined to your lead database where possible.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="recipients-refresh"
          className="btn-secondary text-xs inline-flex items-center gap-1 disabled:opacity-50"
          title="Force-refresh from the webhook event store"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="p-4 border-b border-border flex flex-wrap items-center gap-2">
        <FilterChip testid="filter-all" active={filter === "all"} onClick={() => setFilter("all")} label="All" />
        <FilterChip testid="filter-opened" active={filter === "opened"} onClick={() => setFilter("opened")} icon={Eye} label="Opened" />
        <FilterChip testid="filter-clicked" active={filter === "clicked"} onClick={() => setFilter("clicked")} icon={MousePointerClick} label="Clicked" />
        <FilterChip testid="filter-bounced" active={filter === "bounced"} onClick={() => setFilter("bounced")} icon={MailX} label="Bounced" />
        <FilterChip testid="filter-unsubscribed" active={filter === "unsubscribed"} onClick={() => setFilter("unsubscribed")} icon={MailMinus} label="Unsubscribed" />
        <FilterChip testid="filter-spam" active={filter === "spam"} onClick={() => setFilter("spam")} icon={ShieldAlert} label="Spam" />
        <div className="flex-1 min-w-[200px] relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by email substring"
            className="form-input w-full pl-7 text-xs"
            data-testid="recipients-search"
          />
        </div>
      </div>

      {loading && recipients.length === 0 ? (
        <div className="p-8 text-center text-xs text-muted-foreground inline-flex items-center justify-center gap-2 w-full">
          <Loader2 size={12} className="animate-spin" /> Loading recipients…
        </div>
      ) : recipients.length === 0 ? (
        <div className="p-12 text-center text-xs text-muted-foreground">
          {filter === "all" && !q
            ? "No webhook events recorded for this campaign yet. Once someone opens or clicks, they'll appear here."
            : "No recipients match this filter."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/30 border-b border-border">
              <tr className="text-left">
                <Th>Email</Th>
                <Th>Lead</Th>
                <Th className="text-right" tip="Open count">Opens</Th>
                <Th className="text-right" tip="Click count">Clicks</Th>
                <Th className="text-right" tip="Bounces (hard + soft)">Bnc</Th>
                <Th className="text-right" tip="Unsubscribed">Unsub</Th>
                <Th className="text-right" tip="Spam reports">Spam</Th>
                <Th>Last activity</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <tr
                  key={r.email}
                  data-testid={`recipient-row-${r.email}`}
                  className="border-b border-border/60 hover:bg-secondary/20"
                >
                  <td className="p-2.5 font-mono text-[11px]">{r.email}</td>
                  <td className="p-2.5">
                    {r.in_database ? (
                      <div>
                        <div className="font-medium">{[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}</div>
                        {r.company_name && <div className="text-[10px] text-muted-foreground">{r.company_name}</div>}
                        {r.is_subscribed === false && (
                          <span className="text-[9px] uppercase tracking-wide px-1 py-0.5 mt-0.5 inline-block bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 rounded-sm">
                            Unsubscribed
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-muted-foreground italic">Not in lead DB</span>
                    )}
                  </td>
                  <td className="p-2.5 text-right tabular-nums">{r.opens || "—"}</td>
                  <td className="p-2.5 text-right tabular-nums">{r.clicks || "—"}</td>
                  <td className="p-2.5 text-right tabular-nums">{r.bounces || "—"}</td>
                  <td className="p-2.5 text-right tabular-nums">{r.unsubs || "—"}</td>
                  <td className="p-2.5 text-right tabular-nums">{r.spams || "—"}</td>
                  <td className="p-2.5 text-muted-foreground">{formatTs(r.last_seen)}</td>
                  <td className="p-2.5 text-right">
                    <button
                      onClick={() => setActiveEmail(r.email)}
                      data-testid={`recipient-view-${r.email}`}
                      className="text-primary hover:underline inline-flex items-center gap-0.5"
                      title="View raw event history"
                    >
                      Details <ChevronRight size={10} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeEmail && (
        <RecipientEventsDialog
          campaignId={campaignId}
          email={activeEmail}
          onClose={() => setActiveEmail(null)}
        />
      )}
    </div>
  );
}

function FilterChip({ testid, active, onClick, icon: Icon, label }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-[11px] px-2.5 py-1 rounded-sm border inline-flex items-center gap-1 ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-card text-foreground border-border hover:bg-secondary"
      }`}
    >
      {Icon && <Icon size={10} />} {label}
    </button>
  );
}

function Th({ children, className = "", tip }) {
  return (
    <th className={`p-2.5 font-semibold uppercase tracking-wide text-[10px] text-muted-foreground ${className}`} title={tip}>
      {children}
    </th>
  );
}

function formatTs(ts) {
  if (!ts) return "—";
  try {
    // Mailjet event timestamps are unix-seconds.
    const d = new Date(ts * 1000);
    if (isNaN(d.getTime())) return String(ts);
    return d.toLocaleString();
  } catch { return String(ts); }
}

function RecipientEventsDialog({ campaignId, email, onClose }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/email-campaigns/${campaignId}/recipient/${encodeURIComponent(email)}/events`);
        setEvents(data.events || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load event history");
        setEvents([]);
      }
    })();
  }, [campaignId, email]);

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="recipient-events-backdrop"
    >
      <div
        data-testid="recipient-events-modal"
        className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <div className="meta-label">Recipient activity</div>
            <h3 className="font-display text-lg font-bold mt-0.5 font-mono">{email}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-sm" data-testid="recipient-events-close">
            <X size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {events === null ? (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Loading…
            </div>
          ) : events.length === 0 ? (
            <div className="text-xs text-muted-foreground">No raw events recorded.</div>
          ) : (
            <ul className="space-y-2">
              {events.map((ev, i) => (
                <li key={i} className="border border-border rounded-sm p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold uppercase tracking-wide text-[10px]">{ev.event}</span>
                    <span className="text-muted-foreground text-[10px]">{formatTs(ev.time)}</span>
                  </div>
                  {ev.url && (
                    <div className="mt-1 text-[11px] break-all">
                      <span className="text-muted-foreground">URL: </span>
                      <a href={ev.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{ev.url}</a>
                    </div>
                  )}
                  {ev.error && (
                    <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">Error: {ev.error}</div>
                  )}
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    MessageID: <code className="font-mono">{ev.MessageID}</code>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
